import React, { useState, useEffect } from 'react';
import { Brain, Calendar, Clock, FileJson, ChevronRight, X, Filter, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface AIAnalysis {
  id: string;
  created_at: string;
  product_title: string;
  model: string;
  status: string;
  has_structured_data: boolean;
  has_detailed_description: boolean;
  processing_time_ms: number;
  organization_id?: string;
  user_id?: number;
  user_email?: string;
  user_name?: string;
}

interface AIAnalysisDetail {
  id: string;
  created_at: string;
  organization_id: string;
  user_id?: number;
  user_email?: string;
  user_name?: string;
  product_title: string;
  market_description: string;
  detailed_description: string;
  model: string;
  provider: string;
  status: string;
  processing_time_ms: number;
  total_tokens: number;
  raw_response: any;
  structured_data: any;
  extracted_attributes: any;
  image_metadata?: any;
  error_message: string | null;
}

interface AIResponseHistoryProps {
  organizationId?: string;
}

export const AIResponseHistory: React.FC<AIResponseHistoryProps> = ({ organizationId }) => {
  const { organization } = useAuth();
  const [analyses, setAnalyses] = useState<AIAnalysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AIAnalysisDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonContent, setJsonContent] = useState<any>(null);
  const [jsonTitle, setJsonTitle] = useState('');
  const [viewMode, setViewMode] = useState<'parsed' | 'raw'>('parsed');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['market', 'detailed', 'internal']));

  // Filtering state
  const [organizations, setOrganizations] = useState<Array<{id: string; name: string; display_name: string}>>([]);
  // Default to prop organizationId, fallback to current user's org, or empty for "All Organizations"
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    organizationId || (organization?.id || undefined)
  );
  // Don't set a default start date - show all data by default
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(true); // Show filters by default so users see the org filter
  
  const limit = 20;

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchAnalyses();
  }, [offset, organizationId, selectedOrg, startDate, endDate]);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/ai/organizations', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchAnalyses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });
      
      // Add organization filter only if explicitly selected (not empty string)
      if (selectedOrg && selectedOrg !== '') {
        params.append('organization_id', selectedOrg);
      }
      
      if (startDate) {
        params.append('start_date', startDate);
      }
      
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      const response = await fetch(`/api/ai/analyses?${params}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch AI analyses');
      }
      
      const data = await response.json();
      setAnalyses(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysisDetail = async (analysisId: string) => {
    setDetailLoading(true);
    
    try {
      const response = await fetch(`/api/ai/analyses/${analysisId}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analysis details');
      }
      
      const data = await response.json();
      setSelectedAnalysis(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDetailLoading(false);
    }
  };

  const showJson = (content: any, title: string) => {
    setJsonContent(content);
    setJsonTitle(title);
    setShowJsonModal(true);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'processing':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Response History</h1>
            <p className="text-gray-600">View and analyze AI classification results</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">Filters</span>
            {(selectedOrg || startDate || endDate) && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                Active
              </span>
            )}
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
        </div>
        
        {showFilters && (
          <div className="border-t border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Organization Filter */}
              {organizations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="w-4 h-4 inline-block mr-1" />
                    Organization
                  </label>
                  <select
                    value={selectedOrg || ''}
                    onChange={(e) => {
                      setSelectedOrg(e.target.value || undefined);
                      setOffset(0); // Reset pagination on filter change
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.display_name || org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Start Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline-block mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setOffset(0); // Reset pagination on filter change
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              {/* End Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline-block mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setOffset(0); // Reset pagination on filter change
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Quick Date Filters */}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate('');
                  setOffset(0);
                }}
                className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date(today);
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  setStartDate(weekAgo.toISOString().split('T')[0]);
                  setEndDate('');
                  setOffset(0);
                }}
                className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const monthAgo = new Date(today);
                  monthAgo.setMonth(monthAgo.getMonth() - 1);
                  setStartDate(monthAgo.toISOString().split('T')[0]);
                  setEndDate('');
                  setOffset(0);
                }}
                className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setOffset(0);
                }}
                className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
              >
                All Time
              </button>
            </div>
            
            {/* Clear Filters Button */}
            {(selectedOrg || startDate || endDate) && (
              <div className="mt-2">
                <button
                  onClick={() => {
                    setSelectedOrg(undefined);
                    setStartDate('');
                    setEndDate('');
                    setOffset(0);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Analysis List */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Analyses</h2>
            <p className="text-sm text-gray-600 mt-1">Total: {total} analyses</p>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading analyses...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : analyses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No AI analyses found</div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-[700px] overflow-y-auto">
              {analyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => fetchAnalysisDetail(analysis.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 line-clamp-1">
                        {analysis.product_title}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(analysis.created_at).toLocaleString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDuration(analysis.processing_time_ms)}
                        </span>
                      </div>
                      {analysis.user_name && (
                        <div className="mt-1 text-sm text-gray-500">
                          By: {analysis.user_name}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </span>
                        <span className="text-xs text-gray-500">{analysis.model}</span>
                        {analysis.has_detailed_description && (
                          <span className="text-xs text-green-600">Has Details</span>
                        )}
                      </div>
                      {/* Show organization if admin viewing multiple orgs */}
                      {organizations.length > 1 && analysis.organization_id && (
                        <div className="flex items-center gap-1 mt-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {organizations.find(o => o.id === analysis.organization_id)?.display_name || 
                             organizations.find(o => o.id === analysis.organization_id)?.name || 
                             'Unknown Org'}
                          </span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {total > limit && (
            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Analysis Detail */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            {selectedAnalysis ? (
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedAnalysis.product_title || 'Analysis Details'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Analyzed on {formatDate(selectedAnalysis.created_at)}
                  {selectedAnalysis.user_name && ` by ${selectedAnalysis.user_name}`}
                </p>
              </div>
            ) : (
              <h2 className="text-lg font-semibold text-gray-900">Analysis Details</h2>
            )}
          </div>
          
          {detailLoading ? (
            <div className="p-8 text-center text-gray-500">Loading details...</div>
          ) : selectedAnalysis ? (
            <div className="p-4 space-y-4 max-h-[800px] overflow-y-auto">
              {/* Product Info */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Product Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Title:</span>
                    <p className="font-medium">{selectedAnalysis.product_title || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Market Description:</span>
                    <p className="text-gray-800 mt-1">{selectedAnalysis.market_description || 'N/A'}</p>
                  </div>
                  {selectedAnalysis.detailed_description && (
                    <div>
                      <span className="text-gray-600">Detailed Description:</span>
                      <p className="text-gray-800 mt-1 whitespace-pre-wrap">
                        {selectedAnalysis.detailed_description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Analysis Metadata */}
              <div className="pt-4 border-t">
                <h3 className="font-medium text-gray-900 mb-2">Analysis Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Model:</span>
                    <span className="ml-2 font-medium">{selectedAnalysis.model}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Provider:</span>
                    <span className="ml-2 font-medium">{selectedAnalysis.provider}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Tokens:</span>
                    <span className="ml-2 font-medium">{selectedAnalysis.total_tokens}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Duration:</span>
                    <span className="ml-2 font-medium">{formatDuration(selectedAnalysis.processing_time_ms)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-2 font-medium">{formatDate(selectedAnalysis.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Image Information */}
              {selectedAnalysis.image_metadata && selectedAnalysis.image_metadata.length > 0 && (
                <div className="pt-4 border-t">
                  <h3 className="font-medium text-gray-900 mb-2">Analyzed Images</h3>
                  <div className="space-y-2">
                    {selectedAnalysis.image_metadata.map((img: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-4 text-sm bg-gray-50 p-2 rounded">
                        <span className="font-medium text-gray-700 capitalize">{img.type}:</span>
                        <span className="text-gray-600">{img.filename}</span>
                        <span className="text-xs text-gray-500">
                          ({(img.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Mode Toggle */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Response Data</h3>
                  <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setViewMode('parsed')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'parsed'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Parsed View
                    </button>
                    <button
                      onClick={() => setViewMode('raw')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'raw'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Raw JSON
                    </button>
                  </div>
                </div>

                {/* Content based on view mode */}
                {viewMode === 'parsed' ? (
                  // Parsed Response Structure
                  (() => {
                    try {
                  // Try to parse the structured data if it's a JSON string
                  let parsedData = null;
                  if (selectedAnalysis.structured_data) {
                    if (typeof selectedAnalysis.structured_data === 'string') {
                      parsedData = JSON.parse(selectedAnalysis.structured_data);
                    } else {
                      parsedData = selectedAnalysis.structured_data;
                    }
                  } else if (selectedAnalysis.raw_response) {
                    // Fallback to raw response if structured_data is not available
                    const rawData = typeof selectedAnalysis.raw_response === 'string' 
                      ? JSON.parse(selectedAnalysis.raw_response) 
                      : selectedAnalysis.raw_response;
                    
                    // Check if raw_response has a parsed_content or content field
                    if (rawData.parsed_content) {
                      parsedData = rawData.parsed_content;
                    } else if (rawData.content && typeof rawData.content === 'string') {
                      parsedData = JSON.parse(rawData.content);
                    } else {
                      parsedData = rawData;
                    }
                  }
                  
                  if (parsedData) {
                    return (
                      <div className="pt-4 border-t space-y-4">
                        <h3 className="font-medium text-gray-900">AI Classification Results</h3>
                        
                        {/* SKU Context Note */}
                        {parsedData.sku_context_note && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h4 className="text-sm font-medium text-blue-900 mb-1">SKU Context</h4>
                            <p className="text-sm text-blue-700">{parsedData.sku_context_note}</p>
                          </div>
                        )}
                        
                        {/* Square Projection */}
                        {parsedData.square_projection && (
                          <div className="bg-green-50 p-3 rounded-lg space-y-2">
                            <h4 className="text-sm font-medium text-green-900">Square Catalog Data</h4>
                            
                            {parsedData.square_projection.product_title && (
                              <div>
                                <span className="text-xs text-green-600">Title:</span>
                                <p className="text-sm text-green-800 font-medium">{parsedData.square_projection.product_title}</p>
                              </div>
                            )}
                            
                            {parsedData.square_projection.categories && (
                              <div>
                                <span className="text-xs text-green-600">Categories:</span>
                                <p className="text-sm text-green-800">{parsedData.square_projection.categories.join(', ')}</p>
                              </div>
                            )}
                            
                            {parsedData.square_projection.custom_attributes && (
                              <div>
                                <span className="text-xs text-green-600">Custom Attributes:</span>
                                <div className="mt-1 space-y-1">
                                  {Object.entries(parsedData.square_projection.custom_attributes).map(([key, value]) => (
                                    <div key={key} className="flex gap-2 text-sm">
                                      <span className="text-green-700 font-medium">{key}:</span>
                                      <span className="text-green-800">
                                        {Array.isArray(value) ? value.join(', ') : value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {parsedData.square_projection.market_description && (
                              <div>
                                <span className="text-xs text-green-600">Market Description:</span>
                                <p className="text-sm text-green-800 mt-1 whitespace-pre-wrap">
                                  {parsedData.square_projection.market_description}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Internal Record */}
                        {parsedData.internal_record && (
                          <div className="bg-purple-50 p-3 rounded-lg space-y-2">
                            <h4 className="text-sm font-medium text-purple-900">Internal Classification</h4>
                            
                            {Object.entries(parsedData.internal_record).map(([key, value]) => {
                              if (key === 'attributes_rich' && Array.isArray(value)) {
                                return (
                                  <div key={key}>
                                    <span className="text-xs text-purple-600">Rich Attributes:</span>
                                    <div className="mt-1 space-y-1">
                                      {value.map((attr, idx) => (
                                        <div key={idx} className="text-sm">
                                          <span className="text-purple-700 font-medium">{attr.key}:</span>
                                          <span className="text-purple-800 ml-2">{attr.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              } else if (key === 'detailed_inventory_description') {
                                return (
                                  <div key={key}>
                                    <span className="text-xs text-purple-600">Detailed Description:</span>
                                    <p className="text-sm text-purple-800 mt-1 whitespace-pre-wrap">
                                      {value}
                                    </p>
                                  </div>
                                );
                              } else if (key !== 'attributes_rich' && key !== 'detailed_inventory_description') {
                                return (
                                  <div key={key} className="flex gap-2 text-sm">
                                    <span className="text-purple-700 font-medium">{key.replace(/_/g, ' ')}:</span>
                                    <span className="text-purple-800">
                                      {Array.isArray(value) ? value.join(', ') : value}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                } catch (e) {
                  // If parsing fails, show error message
                  console.error('Error parsing AI response:', e);
                  return (
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                      <p className="text-sm">Error parsing response data. Switch to Raw JSON view to see the original data.</p>
                    </div>
                  );
                }
                
                // Fallback if no data available
                return (
                  <div className="p-4 bg-gray-50 text-gray-600 rounded-lg">
                    <p className="text-sm">No parsed data available. Switch to Raw JSON view to see the original response.</p>
                  </div>
                );
              })()
            ) : (
              // Raw JSON View
              <div className="space-y-2">
                    
                    {selectedAnalysis.structured_data && (
                      <button
                        onClick={() => showJson(selectedAnalysis.structured_data, 'Structured Data')}
                        className="w-full flex items-center justify-between px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <FileJson className="w-4 h-4" />
                          View Structured Data
                        </span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                    
                    {selectedAnalysis.extracted_attributes && (
                      <button
                        onClick={() => showJson(selectedAnalysis.extracted_attributes, 'Extracted Attributes')}
                        className="w-full flex items-center justify-between px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <FileJson className="w-4 h-4" />
                          View Extracted Attributes
                        </span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                    
                    {selectedAnalysis.raw_response && (
                      <button
                        onClick={() => showJson(selectedAnalysis.raw_response, 'Raw Response')}
                        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <FileJson className="w-4 h-4" />
                          View Raw Response
                        </span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Error Message */}
              {selectedAnalysis.error_message && (
                <div className="pt-4 border-t">
                  <h3 className="font-medium text-red-600 mb-2">Error</h3>
                  <p className="text-sm text-red-600">{selectedAnalysis.error_message}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              Select an analysis from the list to view details
            </div>
          )}
        </div>
      </div>

      {/* JSON Modal */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{jsonTitle}</h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto">
              <pre className="text-sm text-gray-800 font-mono bg-gray-50 p-4 rounded-lg">
                {JSON.stringify(jsonContent, null, 2)}
              </pre>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(jsonContent, null, 2));
                  // You could add a toast notification here
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};